import {
  AuditPermissions,
  BookingPermissions,
  LineupPermissions,
  Permission,
  SettingsPermissions,
  TransactionsPermissions
} from '@le2/common-types'
import {
  ClipboardCheckOutline,
  ClipboardListOutline,
  CogOutline,
  DocumentSearchOutline,
  TicketOutline,
  UserGroupOutline
} from 'heroicons-react'
import { Suspense, lazy, useEffect } from 'react'
import { Route, Switch } from 'react-router-dom'
import { useSnapshot } from 'valtio'

import PageNotFound from './components/PageNotFound'
import PrivateRoute from './components/PrivateRoute'
import { LoadingContentPage } from './components/StatusContentPage'
import { NativeExtensionsContextProvider } from './contexts/NativeExtensionsContext'
import UserLayout, { MenuItemProps } from './layouts/user'
import agencyConfig from './lib/agencyConfig'
import { Booking } from './lib/api/booking'
import { useSubscribeHubEvents } from './nativeExtension/hooks'
import OIDCCallback from './pages/OIDCCallback'
import OIDCLogin from './pages/OIDCLogin'
import Error from './pages/error'
import useCreateLineupFromBooking from './pages/lineups/useCreateLineupFromBooking'
import { useLineupSubscribeToBookingActions } from './pages/lineups/useLineupSubscribeToBookingActions'
import Login from './pages/login'
import ViewGroup from './pages/users/ViewGroup'
import { createSystemInfoState, useGetSystemInfo } from './utils/useGetSystemInfo'
import { authUserState } from './utils/usersUtils'

//lazy load these components
const Home = lazy(() => import('./pages/home/'))
const EditProfile = lazy(() => import('./pages/profile/EditProfile'))
const BookingCreate = lazy(() => import('./pages/booking/BookingCreate'))
const BookingView = lazy(() => import('./pages/booking/BookingView'))
const Search = lazy(() => import('./pages/search'))
const Transactions = lazy(() => import('./pages/transactions'))
const Lineups = lazy(() => import('./pages/lineups'))
const LineupGallery = lazy(() => import('./pages/lineups/LineupGallery'))
const LineupPreview = lazy(() => import('./pages/lineups/LineupPreview'))
const LineupView = lazy(() => import('./pages/lineups/LineupView'))
const Audit = lazy(() => import('./pages/audit'))
const Settings = lazy(() => import('./pages/settings'))
const UsersGroups = lazy(() => import('./pages/users'))
const UserInfo = lazy(() => import('./pages/users/UserInfo'))
const OIDC = lazy(() => import('./pages/oidc'))

export interface BookingAction {
  iconMenu: React.ReactElement<any, string>
  iconAs: any
  text: string
  onClick: (booking: Booking) => void
  dialog?: JSX.Element
  isEnabled: (booking: Booking) => boolean
  disableMessage: (booking: Booking) => string
  permission: Permission
}

/**
 * Defines an object to hold callback declarations to subscribe to various booking operations.
 * These methods are called where the respective booking operation executes.
 * For instance, Lineups subscribe to booking update/delete/seal so that the lineups list is refreshed
 * after a booking content is modified or a booking is sealed/unsealed
 */
export interface BookingActionSubscriber {
  onCreate?: (booking?: Booking) => void
  onUpdate?: (booking?: Booking) => void
  onSeal?: (booking?: Booking, sealed?: boolean) => void
  onSubmit?: (booking?: Booking) => void
  onActivate?: (booking?: Booking, activated?: boolean) => void
  onDelete?: (booking?: Booking, deleted?: boolean) => void
}

export default function App() {
  const { data, loading } = useGetSystemInfo()
  const { me } = useSnapshot(authUserState)

  useEffect(() => {
    if (!loading && data) {
      // Creates a global state for the system info given by the server.
      createSystemInfoState(data)
    }
  }, [data, loading])

  // Subscribe to NativeExtension Hub events
  useSubscribeHubEvents()

  const { backgroundChecks } = agencyConfig.features

  // filter PrintTypes based on user and build menu items
  const printTypeSubItems = agencyConfig.features.bookings.userFilteredPrintTypes(me).map((printType) => {
    const params = new URLSearchParams({ 'jfd-value': printType.value })
    return {
      label: printType.label,
      to: `/bookings/new/info/?${params}`
    }
  })

  const menuItems: MenuItemProps[] = [
    {
      label: 'New booking',
      icon: ClipboardListOutline,
      subitems: printTypeSubItems,
      permission: BookingPermissions.BookingCreate
    },
    {
      label: 'Bookings',
      icon: DocumentSearchOutline,
      to: '/search',
      permission: BookingPermissions.BookingView
    },
    ...(backgroundChecks
      ? [
          {
            label: 'Transactions',
            icon: TicketOutline,
            to: '/transactions',
            permission: TransactionsPermissions.TransactionsView
          }
        ]
      : []),

    {
      label: 'Lineups',
      icon: UserGroupOutline,
      to: '/lineups',
      permission: LineupPermissions.LineupView
    },
    {
      label: 'Audit',
      icon: ClipboardCheckOutline,
      permission: AuditPermissions.AuditView,
      to: '/audit'
    },
    {
      label: 'Settings',
      icon: CogOutline,
      subitems: [
        { label: 'Users and Groups', to: '/users', permission: SettingsPermissions.SettingsUsersGroups },
        { label: 'OIDC', to: '/oidc', permission: SettingsPermissions.SettingsOIDC }
      ]
    }
  ]

  const bookingActions = [
    useCreateLineupFromBooking({
      text: 'Create lineup',
      iconMenu: <UserGroupOutline />,
      iconAs: UserGroupOutline
    })
  ]

  // Lineups subscribe to booking update and seal so that the lineups list is refreshed
  // after a booking content is modified or a booking is sealed/unsealed
  const bookingActionSubscribers = [useLineupSubscribeToBookingActions()]

  return (
    <Switch>
      <Route path="/error">
        <Error />
      </Route>
      <Route path="/login">
        <Login />
      </Route>
      <Route path="/OIDCCallback">
        <OIDCCallback />
      </Route>
      <Route path="/OIDCLogin">
        <OIDCLogin />
      </Route>

      <UserLayout menuItems={menuItems}>
        <Suspense fallback={<LoadingContentPage />}>
          <Switch>
            <Route exact path="/">
              <Home />
            </Route>
            <PrivateRoute exact path="/profile" permissions={[SettingsPermissions.SettingsEditUser]}>
              <EditProfile />
            </PrivateRoute>
            <PrivateRoute path="/bookings/new" permissions={[BookingPermissions.BookingCreate]}>
              <BookingCreate />
            </PrivateRoute>
            <PrivateRoute path="/bookings/:bookingNum" permissions={[BookingPermissions.BookingView]}>
              <BookingView bookingActions={bookingActions} bookingActionSubscribers={bookingActionSubscribers} />
            </PrivateRoute>
            <PrivateRoute path="/search" permissions={[BookingPermissions.BookingView]}>
              <Search bookingActions={bookingActions} />
            </PrivateRoute>
            {backgroundChecks && (
              <PrivateRoute path="/transactions" permissions={[TransactionsPermissions.TransactionsView]}>
                <NativeExtensionsContextProvider>
                  <Transactions />
                </NativeExtensionsContextProvider>
              </PrivateRoute>
            )}
            <PrivateRoute path="/lineups" permissions={[LineupPermissions.LineupView]} exact>
              <Lineups />
            </PrivateRoute>
            <PrivateRoute path="/lineups/new" permissions={[LineupPermissions.LineupView]} exact>
              <LineupGallery />
            </PrivateRoute>
            <PrivateRoute path="/lineups/new/preview" permissions={[LineupPermissions.LineupView]}>
              <LineupPreview />
            </PrivateRoute>
            <PrivateRoute path="/lineups/:lineupNum" permissions={[LineupPermissions.LineupView]}>
              <LineupView />
            </PrivateRoute>
            <PrivateRoute path="/audit" permissions={[AuditPermissions.AuditView]}>
              <Audit />
            </PrivateRoute>
            <PrivateRoute
              path="/settings"
              permissions={[SettingsPermissions.SettingsUsersGroups, SettingsPermissions.SettingsOIDC]}
            >
              <Settings />
            </PrivateRoute>
            <PrivateRoute path="/users" permissions={[SettingsPermissions.SettingsUsersGroups]}>
              <UsersGroups />
            </PrivateRoute>
            <PrivateRoute path="/user/info" permissions={[SettingsPermissions.SettingsUsersGroups]} exact={true}>
              <UserInfo />
            </PrivateRoute>
            <PrivateRoute path="/view-group" permissions={[SettingsPermissions.SettingsUsersGroups]}>
              <ViewGroup />
            </PrivateRoute>
            <PrivateRoute path="/oidc" permissions={[SettingsPermissions.SettingsOIDC]}>
              <OIDC />
            </PrivateRoute>
            <Route path="*" render={() => <PageNotFound />} />
          </Switch>
        </Suspense>
      </UserLayout>
    </Switch>
  )
}
